/**
 * useCountdown Hook
 *
 * Custom hook for countdown timer functionality.
 * Used by MFA components for rate limiting countdowns.
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Countdown hook that manages a timer countdown
 *
 * @param initialSeconds - Initial countdown value in seconds
 * @returns Current countdown value (0 when finished)
 */
export function useCountdown(initialSeconds: number): number {
  const [countdown, setCountdown] = useState(initialSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Reset countdown when initialSeconds changes
    setCountdown(initialSeconds);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't start timer if initial value is 0 or negative
    if (initialSeconds <= 0) return;

    // Start countdown interval
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          // Clear interval when countdown reaches 0
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return next;
      });
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [initialSeconds]);

  return countdown;
}
